import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Activity, Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import TwoFactorVerify from '../components/Auth/TwoFactorVerify';
import api from '../config/axios';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');

  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/api/auth/login', {
        email: formData.email,
        password: formData.password
      });

      if (response.data.requires2FA) {
        setTempToken(response.data.tempToken);
        setRequires2FA(true);
        toast.info('2FA verification required');
        setLoading(false);
        return;
      }

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      window.dispatchEvent(new Event('storage'));

      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerified = async (code, trustDevice) => {
    try {
      const response = await api.post('/api/auth/2fa/verify-login', {
        tempToken,
        code,
        trustDevice
      });

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      window.dispatchEvent(new Event('storage'));

      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.error || '2FA verification failed');
      throw error;
    }
  };

  const handle2FACancel = () => {
    setRequires2FA(false);
    setTempToken('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950 px-4 sm:px-6 lg:px-8 mesh-bg">
      <div className="max-w-md w-full animate-slide-up">
        <div className="card-premium p-8 lg:p-10">
          <div className="text-center mb-10">
            <div className="flex justify-center mb-6">
              <Link to="/" className="p-3 bg-brand-600 rounded-2xl shadow-glow-brand animate-float hover:scale-110 transition-transform">
                <Activity className="h-10 w-10 text-white" />
              </Link>
            </div>
            <h2 className="text-3xl font-bold text-surface-900 dark:text-white mb-2">
              Welcome back
            </h2>
            <p className="text-surface-600 dark:text-surface-400">
              Enter your credentials to access your dashboard
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-surface-700 dark:text-surface-300 mb-2">
                  Email Address
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-surface-400 group-focus-within:text-brand-500 transition-colors" />
                  </div>
                  <input
                    name="email"
                    type="email"
                    required
                    className="input-premium pl-12"
                    placeholder="name@company.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-surface-700 dark:text-surface-300">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-bold text-brand-600 dark:text-brand-400 hover:text-brand-500 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-surface-400 group-focus-within:text-brand-500 transition-colors" />
                  </div>
                  <input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    className="input-premium pl-12 pr-12"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-surface-400 hover:text-surface-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-surface-400 hover:text-surface-600" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3.5 text-base flex items-center justify-center font-bold"
            >
              {loading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  Sign In <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </button>

            <div className="text-center pt-2">
              <p className="text-sm text-surface-600 dark:text-surface-400">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="font-bold text-brand-600 dark:text-brand-400 hover:underline"
                >
                  Create Account
                </Link>
              </p>
            </div>
          </form>
        </div>

        <p className="mt-8 text-center text-xs text-surface-500 tracking-widest uppercase">
          &copy; {new Date().getFullYear()} UptimeMonitor Infrastructure
        </p>
      </div>

      {requires2FA && (
        <TwoFactorVerify
          tempToken={tempToken}
          onVerified={handle2FAVerified}
          onCancel={handle2FACancel}
        />
      )}
    </div>
  );
};

export default Login;
